import type { PMIssue } from "@/domains/pm/types";
import { toISODate } from "./localDate";

/**
 * Whether an issue's due date should render as "overdue" (red).
 *
 * Only issues that are still live work and past their due date count. Finished
 * ones are history, so a past due date is not actionable and must never be
 * highlighted — the red colour is a cue to spot work that still needs
 * attention.
 *
 * "Live work" is no longer the literal `in_progress`: a project can define its
 * own statuses, and one of them ("Blocked", "Awaiting approval") is usually
 * open. Pass `isOpen` from the status catalogue —
 * `(s) => isOpenStatus(catalogue, issue.building_project_id, s)`. The default
 * keeps the built-in reading for callers with no catalogue to hand.
 */
export function isIssueOverdue(
  issue: Pick<PMIssue, "status" | "due_date">,
  isOpen: (status: string) => boolean = (s) => s === "in_progress"
): boolean {
  if (!isOpen(issue.status) || !issue.due_date) return false;
  const due = new Date(issue.due_date);
  due.setHours(23, 59, 59, 999);
  return due < new Date();
}

/**
 * The `PMIssueFilters.dueBefore` cutoff matching isIssueOverdue: today's LOCAL
 * date as YYYY-MM-DD. An open issue is overdue exactly when its
 * due_date (a date-only string) sorts strictly before this — letting the
 * server filter overdue issues instead of narrowing a paginated list client-side.
 */
export function overdueDueBefore(now = new Date()): string {
  return toISODate(now);
}
